import React from 'react';
import { cn } from '../lib/utils';

const CityCard = ({ 
  card, 
  isSelected = false, 
  isPlayable = true, 
  showRank = false, 
  deckColors = null,
  onClick,
  className = '',
  isAnimating = false,
  animationType = null
}) => {
  // Default colors if no deck colors provided
  const colors = deckColors || {
    border: "border-gray-300",
    from: "from-blue-500",
    to: "to-purple-600"
  };

  // Dynamic font sizing based on name length
  const getNameFontSize = () => {
    const nameLength = card.name.length;
    if (nameLength > 20) return 'text-[9px]';  // Very long names
    if (nameLength > 15) return 'text-[10px]'; // Long names
    if (nameLength > 10) return 'text-[11px]'; // Medium names
    return 'text-xs';                           // Short names (default)
  };

  // Animation classes based on result
  const getAnimationClasses = () => {
    if (!isAnimating) return '';
    
    if (animationType === 'winner') {
      return 'animate-pulse ring-4 ring-green-400 shadow-2xl shadow-green-400/50 scale-110';
    } else if (animationType === 'loser') {
      return 'animate-fade-shrink opacity-30 scale-90';
    }
    return '';
  };

  return (
    <div 
      className={cn(
        "relative", // Wrapper handles positioning
        !className.includes('w-') && "w-[62px]", // Default width if not provided
        !className.includes('h-') && "h-[92px]", // Default height if not provided
        className
      )}
      onClick={isPlayable ? onClick : undefined}
    >
      <div className={cn(
        "w-full h-full absolute top-0 left-0", // Fill wrapper
        "rounded-xl border-2 shadow-lg transition-all duration-300 origin-center", // Visuals + Transition
        "flex flex-col items-center justify-between p-2",
        "overflow-hidden bg-white", 
        isSelected ? "border-green-400 ring-2 ring-green-400 transform scale-105" : colors.border,
        // Special gold treatment for #1 ranked cards
        card.rank === 1 && "!border-yellow-400 ring-4 ring-yellow-400/60 shadow-2xl shadow-yellow-400/40",
        // Removed zoom effect as per user request
        isPlayable ? "cursor-pointer" : "opacity-60 cursor-not-allowed",
        !card.image && `bg-gradient-to-br ${colors.from} ${colors.to}`,
        getAnimationClasses()
      )}>
        {/* Subtle Gold Corner for #1 Cards */}
        {card.rank === 1 && (
          <div className="absolute top-0 right-0 w-0 h-0 pointer-events-none z-30"
               style={{
                 borderStyle: 'solid',
                 borderWidth: '0 20px 20px 0',
                 borderColor: 'transparent #facc15 transparent transparent'
               }}>
          </div>
        )}
        {/* Card Image or Gradient Background */}
        {card.image ? (
          <>
            <img 
              src={card.image} 
              alt={card.name}
              className="absolute inset-0 w-full h-full object-cover rounded-xl"
            />
            {/* Darker gradient overlay for better text visibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60 rounded-xl"></div>
          </>
        ) : (
          <div className="absolute inset-0 opacity-20">
            <div className="w-full h-full bg-gradient-to-br from-white/20 to-transparent"></div>
          </div>
        )}
        
        {/* Card Content */}
        <div className="relative z-10 flex flex-col items-center justify-between h-full w-full">
          {/* X-Ray Vision Rank Display - Top of card */}
          {showRank && (
            <div className="mt-1 px-2 py-1 bg-cyan-600/90 backdrop-blur-sm rounded-lg">
              <span className="text-sm sm:text-base font-bold text-white">
                #{card.rank}
              </span>
            </div>
          )}
          
          {/* Card Name - with background for visibility */}
          <div className="mt-auto w-full text-center px-1 py-1 bg-black/60 backdrop-blur-sm rounded-lg">
            <h3 className={cn(getNameFontSize(), "font-bold leading-tight text-white drop-shadow-lg whitespace-pre-line")} style={{textShadow: '1px 1px 2px rgba(0,0,0,0.8)'}}>
              {card.name}
            </h3>
          </div>
          
          {/* Card Stats/Info */}
          {card.population && (
            <div className="text-center text-white">
              <div className="text-xs font-semibold drop-shadow-md">
                {card.population.toLocaleString()}
              </div>
            </div>
          )}
        </div>
        
        {/* Card shine effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent pointer-events-none rounded-xl"></div>
      </div>
    </div>
  );
};

export default CityCard;
